import { useState, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  ArrowLeft,
  Building2,
  Users,
  Package,
  ShoppingCart,
  TrendingUp,
  TrendingDown,
  DollarSign,
  CreditCard,
  Clock,
  Save,
  Calendar,
  Activity,
  BarChart3,
  PieChart,
  LineChart as LineChartIcon,
  Target,
  AlertCircle,
  CheckCircle2,
  XCircle,
  FileText,
  Database,
  Zap,
  Globe,
  Mail,
  Phone,
  MapPin,
  Percent,
  Award,
  Wallet,
  Receipt,
  UserCheck,
  UserX,
  PackageCheck,
  PackageX,
  ShoppingBag,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  Eye,
  FileBarChart,
} from "lucide-react";
import { toast } from "sonner";
import { format, startOfMonth, endOfMonth, subMonths, startOfDay, endOfDay, parseISO, subDays, differenceInDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  ComposedChart,
} from "recharts";

type Tenant = {
  id: string;
  name: string;
  slug: string;
  email: string | null;
  status: string;
  trial_ends_at: string | null;
  created_at: string;
  updated_at: string | null;
  usage: {
    users: number;
  } | null;
  subscription_status: string | null;
  subscription_plan_id: string | null;
  subscription_type: string | null;
  subscription_payment_method: string | null;
  subscription_start_date: string | null;
  subscription_next_billing_date: string | null;
  subscription_last_payment_date: string | null;
  colors: any;
  images: any;
  chatwoot_account_id: number | null;
};

type TenantStats = {
  // Contadores básicos
  totalUsers: number;
  activeUsers: number;
  inactiveUsers: number;
  totalProducts: number;
  activeProducts: number;
  inactiveProducts: number;
  totalClients: number;
  totalSuppliers: number;
  totalTransactions: number;
  totalSales: number;
  totalExpenses: number;
  
  // Financeiro
  totalRevenue: number;
  totalExpenseValue: number;
  netProfit: number;
  profitMargin: number;
  averageTicket: number;
  averageExpense: number;
  
  // Vendas
  totalSalesValue: number;
  totalSalesItems: number;
  averageItemsPerSale: number;
  salesGrowth: number;
  revenueGrowth: number;
  
  // Despesas
  totalExpensesCount: number;
  expensesGrowth: number;
  
  // Uso e atividade
  daysSinceCreation: number;
  daysActive: number;
  activityRate: number;
  lastActivity: string | null;
  averageTransactionsPerDay: number;
  averageUsersPerDay: number;
  
  // Temporal
  monthlyRevenue: { month: string; revenue: number; expenses: number; profit: number }[];
  weeklyActivity: { week: string; transactions: number; users: number }[];
  dailyActivity: { day: string; transactions: number; revenue: number }[];
  hourlyDistribution: { hour: number; transactions: number }[];
  
  // Top performers
  topUsers: { name: string; sales: number; transactions: number }[];
  topProducts: { name: string; quantity: number; revenue: number }[];
  topClients: { name: string; purchases: number; value: number }[];
  topSuppliers: { name: string; expenses: number; value: number }[];
  
  // Categorias
  productCategories: { category: string; count: number }[];
  expenseCategories: { category: string; count: number; value: number }[];
  
  // Métodos de pagamento
  paymentMethods: { method: string; value: number; count: number }[];
  
  // Status de transações
  transactionStatus: { status: string; count: number; value: number }[];
  
  // Análise de crescimento (mensal - 30 dias)
  userGrowth: number;
  productGrowth: number;
  clientGrowth: number;
  transactionGrowth: number;
  
  // Análise de crescimento por período
  dailyGrowth: {
    sales: number;
    users: number;
    products: number;
    clients: number;
    transactions: number;
  };
  weeklyGrowth: {
    sales: number;
    users: number;
    products: number;
    clients: number;
    transactions: number;
  };
  biweeklyGrowth: {
    sales: number;
    users: number;
    products: number;
    clients: number;
    transactions: number;
  };
  monthlyGrowth: {
    sales: number;
    users: number;
    products: number;
    clients: number;
    transactions: number;
  };
  
  // Distribuição temporal
  transactionsByDayOfWeek: { day: string; count: number; value: number }[];
  transactionsByMonth: { month: string; count: number; value: number }[];
  
  // Análise de uso
  usersWithSales: number;
  usersWithoutSales: number;
  productsWithSales: number;
  productsWithoutSales: number;
  clientsWithPurchases: number;
  clientsWithoutPurchases: number;
  
  // Frequência
  averageDaysBetweenSales: number;
  averageDaysBetweenExpenses: number;
  mostActiveDay: string | null;
  mostActiveHour: number | null;
  
  // Insights
  churnRisk: number;
  engagementScore: number;
  healthScore: number;
};

const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined || isNaN(value)) return "R$ 0,00";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

export default function TenantDetails(props?: { id?: string; embedded?: boolean; periodRange?: [Date, Date]; setLoading?: (loading: boolean) => void }) {
  const { id: routeId } = useParams<{ id: string }>();
  const id = props?.id ?? routeId;
  const navigate = useNavigate();
  const location = useLocation();
  const isEmbedded = (typeof props?.embedded !== 'undefined') ? !!props?.embedded : (new URLSearchParams(location.search).get("embedded") === "1");
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const search = new URLSearchParams(location.search);
  const fromStr = search.get("from");
  const toStr = search.get("to");
  const cadFrom = props?.periodRange ? startOfDay(props.periodRange[0]) : (fromStr ? startOfDay(new Date(fromStr)) : startOfDay(new Date(0)));
  const cadTo = props?.periodRange ? endOfDay(props.periodRange[1]) : (toStr ? endOfDay(new Date(toStr)) : endOfDay(new Date()));

  // Buscar tenant
  const { data: tenant, isLoading: tenantLoading } = useQuery({
    queryKey: ["tenant", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tenants" as any)
        .select("*")
        .eq("id", id)
        .maybeSingle();
      if (error) throw error;
      return data as unknown as Tenant | null;
    },
    enabled: !!id,
  });

  // Buscar todos os dados do tenant
  const { data: allData, isLoading: dataLoading } = useQuery({
    queryKey: ["tenant-all-data", id],
    queryFn: async () => {
      if (!id) {
        console.log("TenantDetails: ID não fornecido");
        return null;
      }

      console.log("TenantDetails: Iniciando busca de dados para tenant:", id);

      try {
        // Buscar usuários
        const { data: users, error: usersError } = await supabase
          .from("profiles" as any)
          .select("*")
          .eq("tenant_id", id);
        if (usersError) {
          console.error("Erro ao buscar usuários:", usersError);
        } else {
          console.log("TenantDetails: Usuários encontrados:", users?.length || 0);
        }

        // Buscar produtos
        const { data: products, error: productsError } = await supabase
          .from("products" as any)
          .select("*")
          .eq("tenant_id", id);
        if (productsError) {
          console.error("Erro ao buscar produtos:", productsError);
        } else {
          console.log("TenantDetails: Produtos encontrados:", products?.length || 0);
        }

        // Buscar clientes
        const { data: clients, error: clientsError } = await supabase
          .from("clients" as any)
          .select("*")
          .eq("tenant_id", id);
        if (clientsError) {
          console.error("Erro ao buscar clientes:", clientsError);
        } else {
          console.log("TenantDetails: Clientes encontrados:", clients?.length || 0);
        }

        // Buscar fornecedores
        const { data: suppliers, error: suppliersError } = await supabase
          .from("suppliers" as any)
          .select("*")
          .eq("tenant_id", id);
        if (suppliersError) {
          console.error("Erro ao buscar fornecedores:", suppliersError);
        } else {
          console.log("TenantDetails: Fornecedores encontrados:", suppliers?.length || 0);
        }

        // Buscar transações
        const { data: transactions, error: transactionsError } = await supabase
          .from("transactions" as any)
          .select(`
            *,
            clients (nome),
            suppliers (nome),
            profiles:vendedor_id (nome_completo),
            transaction_items (
              id,
              quantidade,
              preco_unitario,
              total,
              products (nome)
            )
          `)
          .eq("tenant_id", id)
          .gte("created_at", cadFrom.toISOString())
          .lte("created_at", cadTo.toISOString())
          .order("created_at", { ascending: false });
        if (transactionsError) {
          console.error("Erro ao buscar transações:", transactionsError);
        } else {
          console.log("TenantDetails: Transações encontradas:", transactions?.length || 0);
        }

        // Buscar pagamentos - buscar diretamente pelo tenant_id
        const transactionIds = (transactions || []).map((t: any) => t.id);
        const { data: pays, error: paymentsError } = await supabase
          .from("transaction_payments" as any)
          .select(`
            *,
            payment_methods:payment_method_id (nome)
          `)
          .in("transaction_id", transactionIds);
        if (paymentsError) {
          console.error("Erro ao buscar pagamentos:", paymentsError);
        } else {
          console.log("TenantDetails: Pagamentos encontrados:", pays?.length || 0);
        }
        const payments = pays || [];

        const result = {
          users: users || [],
          products: products || [],
          clients: clients || [],
          suppliers: suppliers || [],
          transactions: transactions || [],
          payments: payments || [],
        };

        console.log("TenantDetails: Dados retornados:", {
          users: result.users.length,
          products: result.products.length,
          clients: result.clients.length,
          suppliers: result.suppliers.length,
          transactions: result.transactions.length,
          payments: result.payments.length,
        });

        return result;
      } catch (error) {
        console.error("Erro ao buscar dados do tenant:", error);
        return {
          users: [],
          products: [],
          clients: [],
          suppliers: [],
          transactions: [],
          payments: [],
        };
      }
    },
    enabled: !!id,
  });

  // Calcular estatísticas
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ["tenant-stats", id, allData, tenant],
    queryFn: async () => {
      if (!allData || !tenant) {
        console.log("TenantDetails: Stats - allData ou tenant não disponível", {
          hasAllData: !!allData,
          hasTenant: !!tenant,
        });
        return null;
      }

      console.log("TenantDetails: Calculando estatísticas com dados:", {
        users: allData.users?.length || 0,
        products: allData.products?.length || 0,
        clients: allData.clients?.length || 0,
        suppliers: allData.suppliers?.length || 0,
        transactions: allData.transactions?.length || 0,
        payments: allData.payments?.length || 0,
      });

      const { users = [], products = [], clients = [], suppliers = [], transactions = [], payments = [] } = allData;

      // Contadores básicos
      const totalUsers = (users || []).length;
      const activeUsers = (users || []).filter((u: any) => u.status === "ativo").length;
      const inactiveUsers = totalUsers - activeUsers;

      const totalProducts = (products || []).length;
      const activeProducts = (products || []).filter((p: any) => p.status === "ativo").length;
      const inactiveProducts = totalProducts - activeProducts;

      const totalClients = (clients || []).length;
      const totalSuppliers = (suppliers || []).length;
      const totalTransactions = (transactions || []).length;

      // Separar vendas e despesas
      const sales = (transactions || []).filter((t: any) => t.tipo === "entrada");
      const expenses = (transactions || []).filter((t: any) => t.tipo === "saida");
      const totalSales = sales.length;
      const totalExpenses = expenses.length;

      // Financeiro
      const totalRevenue = sales.reduce((sum: number, t: any) => sum + Number(t.valor_liquido || 0), 0);
      const totalExpenseValue = expenses.reduce((sum: number, t: any) => sum + Number(t.valor_liquido || 0), 0);
      const netProfit = totalRevenue - totalExpenseValue;
      const profitMargin = totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : 0;
      const averageTicket = totalSales > 0 ? totalRevenue / totalSales : 0;
      const averageExpense = totalExpenses > 0 ? totalExpenseValue / totalExpenses : 0;

      // Vendas
      const totalSalesValue = totalRevenue;
      const totalSalesItems = sales.reduce((sum: number, t: any) => {
        return sum + (t.transaction_items?.length || 0);
      }, 0);
      const averageItemsPerSale = totalSales > 0 ? totalSalesItems / totalSales : 0;

      // Análise temporal
      const now = new Date();
      const createdDate = new Date((tenant as any).created_at);
      const daysSinceCreation = differenceInDays(now, createdDate);
      
      // Última atividade
      const lastTransaction = (transactions || []).length > 0 ? transactions[0] : null;
      const lastActivity = (lastTransaction as any)?.created_at || null;

      // Calcular dias ativos (dias com pelo menos uma transação)
      const transactionDates = new Set(
        (transactions || []).map((t: any) => format(new Date(t.created_at), "yyyy-MM-dd"))
      );
      const daysActive = transactionDates.size;
      const activityRate = daysSinceCreation > 0 ? (daysActive / daysSinceCreation) * 100 : 0;
      const averageTransactionsPerDay = daysActive > 0 ? totalTransactions / daysActive : 0;
      const averageUsersPerDay = daysActive > 0 ? totalUsers / daysActive : 0;

      // Vendas mensais (últimos 12 meses)
      const monthlyMap = new Map<string, { revenue: number; expenses: number }>();
      (sales || []).forEach((sale: any) => {
        const month = format(new Date(sale.created_at), "MMM/yy", { locale: ptBR });
        const current = monthlyMap.get(month) || { revenue: 0, expenses: 0 };
        monthlyMap.set(month, {
          revenue: current.revenue + Number(sale.valor_liquido || 0),
          expenses: current.expenses,
        });
      });
      (expenses || []).forEach((expense: any) => {
        const month = format(new Date(expense.created_at), "MMM/yy", { locale: ptBR });
        const current = monthlyMap.get(month) || { revenue: 0, expenses: 0 };
        monthlyMap.set(month, {
          revenue: current.revenue,
          expenses: current.expenses + Number(expense.valor_liquido || 0),
        });
      });
      const monthlyRevenue = Array.from(monthlyMap.entries())
        .map(([month, data]) => ({
          month,
          revenue: data.revenue,
          expenses: data.expenses,
          profit: data.revenue - data.expenses,
        }))
        .slice(-12);

      // Função auxiliar para calcular crescimento em um período
      const calculateGrowth = (
        periodDays: number,
        data: any[],
        dateField: string,
        valueField?: string
      ) => {
        const now = new Date();
        const periodStart = subDays(now, periodDays);
        const previousPeriodStart = subDays(now, periodDays * 2);
        const previousPeriodEnd = subDays(now, periodDays);

        const recent = data.filter((item: any) => {
          const date = new Date(item[dateField]);
          return date >= periodStart;
        });

        const previous = data.filter((item: any) => {
          const date = new Date(item[dateField]);
          return date >= previousPeriodStart && date < previousPeriodEnd;
        });

        if (valueField) {
          const recentValue = recent.reduce((sum: number, item: any) => sum + Number(item[valueField] || 0), 0);
          const previousValue = previous.reduce((sum: number, item: any) => sum + Number(item[valueField] || 0), 0);
          return previousValue > 0 ? ((recentValue - previousValue) / previousValue) * 100 : 0;
        } else {
          return previous.length > 0 ? ((recent.length - previous.length) / previous.length) * 100 : 0;
        }
      };

      // Crescimento mensal (30 dias vs 30 dias anteriores) - mantido para compatibilidade
      const thirtyDaysAgo = subDays(now, 30);
      const sixtyDaysAgo = subDays(now, 60);
      
      const recentSales = sales.filter((s: any) => new Date(s.created_at) >= thirtyDaysAgo);
      const olderSales = sales.filter((s: any) => {
        const date = new Date(s.created_at);
        return date >= sixtyDaysAgo && date < thirtyDaysAgo;
      });
      
      const recentRevenue = recentSales.reduce((sum: number, s: any) => sum + Number(s.valor_liquido || 0), 0);
      const olderRevenue = olderSales.reduce((sum: number, s: any) => sum + Number(s.valor_liquido || 0), 0);
      const salesGrowth = olderRevenue > 0 ? ((recentRevenue - olderRevenue) / olderRevenue) * 100 : 0;
      const revenueGrowth = salesGrowth;

      const recentExpenses = expenses.filter((e: any) => new Date(e.created_at) >= thirtyDaysAgo);
      const olderExpenses = expenses.filter((e: any) => {
        const date = new Date(e.created_at);
        return date >= sixtyDaysAgo && date < thirtyDaysAgo;
      });
      const expensesGrowth = olderExpenses.length > 0
        ? ((recentExpenses.length - olderExpenses.length) / olderExpenses.length) * 100
        : 0;

      // Top usuários (vendedores)
      const userSalesMap = new Map<string, { name: string; sales: number; transactions: number }>();
      (sales || []).forEach((sale: any) => {
        const userId = sale.vendedor_id;
        const userName = sale.profiles?.nome_completo || "Sem vendedor";
        if (userId) {
          const current = userSalesMap.get(userId) || { name: userName, sales: 0, transactions: 0 };
          userSalesMap.set(userId, {
            name: userName,
            sales: current.sales + Number(sale.valor_liquido || 0),
            transactions: current.transactions + 1,
          });
        }
      });
      const topUsers = Array.from(userSalesMap.values())
        .sort((a, b) => b.sales - a.sales)
        .slice(0, 10);

      // Top produtos
      const productMap = new Map<string, { name: string; quantity: number; revenue: number }>();
      (sales || []).forEach((sale: any) => {
        sale.transaction_items?.forEach((item: any) => {
          const productName = item.products?.nome || "Produto Desconhecido";
          const current = productMap.get(productName) || { name: productName, quantity: 0, revenue: 0 };
          productMap.set(productName, {
            name: productName,
            quantity: current.quantity + item.quantidade,
            revenue: current.revenue + Number(item.total || 0),
          });
        });
      });
      const topProducts = Array.from(productMap.values())
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 10);

      // Top clientes
      const clientMap = new Map<string, { name: string; purchases: number; value: number }>();
      (sales || []).forEach((sale: any) => {
        if (sale.client_id && sale.clients) {
          const clientName = sale.clients.nome;
          const current = clientMap.get(sale.client_id) || { name: clientName, purchases: 0, value: 0 };
          clientMap.set(sale.client_id, {
            name: clientName,
            purchases: current.purchases + 1,
            value: current.value + Number(sale.valor_liquido || 0),
          });
        }
      });
      const topClients = Array.from(clientMap.values())
        .sort((a, b) => b.value - a.value)
        .slice(0, 10);

      // Top fornecedores
      const supplierMap = new Map<string, { name: string; expenses: number; value: number }>();
      (expenses || []).forEach((expense: any) => {
        if (expense.supplier_id && expense.suppliers) {
          const supplierName = expense.suppliers.nome;
          const current = supplierMap.get(expense.supplier_id) || { name: supplierName, expenses: 0, value: 0 };
          supplierMap.set(expense.supplier_id, {
            name: supplierName,
            expenses: current.expenses + 1,
            value: current.value + Number(expense.valor_liquido || 0),
          });
        }
      });
      const topSuppliers = Array.from(supplierMap.values())
        .sort((a, b) => b.value - a.value)
        .slice(0, 10);

      // Métodos de pagamento
      const paymentMethodMap = new Map<string, { method: string; value: number; count: number }>();
      (payments || []).forEach((payment: any) => {
        if (payment.status === "confirmado") {
          const methodName = payment.payment_methods?.nome || "Não especificado";
          const current = paymentMethodMap.get(methodName) || { method: methodName, value: 0, count: 0 };
          paymentMethodMap.set(methodName, {
            method: methodName,
            value: current.value + Number(payment.valor || 0),
            count: current.count + 1,
          });
        }
      });
      const paymentMethods = Array.from(paymentMethodMap.values())
        .sort((a, b) => b.value - a.value);

      // Status de transações
      const statusMap = new Map<string, { status: string; count: number; value: number }>();
      (transactions || []).forEach((t: any) => {
        const status = t.status || "pendente";
        const current = statusMap.get(status) || { status, count: 0, value: 0 };
        statusMap.set(status, {
          status,
          count: current.count + 1,
          value: current.value + Number(t.valor_liquido || 0),
        });
      });
      const transactionStatus = Array.from(statusMap.values());

      // Distribuição por dia da semana
      const dayOfWeekMap = new Map<string, { day: string; count: number; value: number }>();
      const weekDays = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
      (transactions || []).forEach((t: any) => {
        const dayIndex = new Date(t.created_at).getDay();
        const day = weekDays[dayIndex];
        const current = dayOfWeekMap.get(day) || { day, count: 0, value: 0 };
        dayOfWeekMap.set(day, {
          day,
          count: current.count + 1,
          value: current.value + Number(t.valor_liquido || 0),
        });
      });
      const transactionsByDayOfWeek = weekDays.map((day) => {
        const data = dayOfWeekMap.get(day) || { day, count: 0, value: 0 };
        return data;
      });

      // Distribuição por hora
      const hourMap = new Map<number, number>();
      (transactions || []).forEach((t: any) => {
        const hour = new Date(t.created_at).getHours();
        hourMap.set(hour, (hourMap.get(hour) || 0) + 1);
      });
      const hourlyDistribution = Array.from({ length: 24 }, (_, i) => ({
        hour: i,
        transactions: hourMap.get(i) || 0,
      }));

      // Análise de uso
      const usersWithSales = new Set(sales.map((s: any) => s.vendedor_id).filter(Boolean)).size;
      const usersWithoutSales = totalUsers - usersWithSales;

      const productsWithSales = productMap.size;
      const productsWithoutSales = totalProducts - productsWithSales;

      const clientsWithPurchases = clientMap.size;
      const clientsWithoutPurchases = totalClients - clientsWithPurchases;

      // Crescimento de entidades (mensal - 30 dias)
      const thirtyDaysAgoDate = subDays(now, 30);
      const sixtyDaysAgoDate = subDays(now, 60);

      const recentUsers = (users || []).filter((u: any) => new Date(u.created_at) >= thirtyDaysAgoDate).length;
      const olderUsers = (users || []).filter((u: any) => {
        const date = new Date(u.created_at);
        return date >= sixtyDaysAgoDate && date < thirtyDaysAgoDate;
      }).length;
      const userGrowth = olderUsers > 0 ? ((recentUsers - olderUsers) / olderUsers) * 100 : 0;

      const recentProducts = (products || []).filter((p: any) => new Date(p.created_at) >= thirtyDaysAgoDate).length;
      const olderProducts = (products || []).filter((p: any) => {
        const date = new Date(p.created_at);
        return date >= sixtyDaysAgoDate && date < thirtyDaysAgoDate;
      }).length;
      const productGrowth = olderProducts > 0 ? ((recentProducts - olderProducts) / olderProducts) * 100 : 0;

      const recentClients = (clients || []).filter((c: any) => new Date(c.created_at) >= thirtyDaysAgoDate).length;
      const olderClients = (clients || []).filter((c: any) => {
        const date = new Date(c.created_at);
        return date >= sixtyDaysAgoDate && date < thirtyDaysAgoDate;
      }).length;
      const clientGrowth = olderClients > 0 ? ((recentClients - olderClients) / olderClients) * 100 : 0;

      const transactionGrowth = olderSales.length > 0
        ? ((recentSales.length - olderSales.length) / olderSales.length) * 100
        : 0;

      // Crescimento por período (diário, semanal, quinzenal, mensal)
      const dailyGrowth = {
        sales: calculateGrowth(1, sales, "created_at", "valor_liquido"),
        users: calculateGrowth(1, users || [], "created_at"),
        products: calculateGrowth(1, products || [], "created_at"),
        clients: calculateGrowth(1, clients || [], "created_at"),
        transactions: calculateGrowth(1, transactions || [], "created_at"),
      };

      const weeklyGrowth = {
        sales: calculateGrowth(7, sales, "created_at", "valor_liquido"),
        users: calculateGrowth(7, users || [], "created_at"),
        products: calculateGrowth(7, products || [], "created_at"),
        clients: calculateGrowth(7, clients || [], "created_at"),
        transactions: calculateGrowth(7, transactions || [], "created_at"),
      };

      const biweeklyGrowth = {
        sales: calculateGrowth(15, sales, "created_at", "valor_liquido"),
        users: calculateGrowth(15, users || [], "created_at"),
        products: calculateGrowth(15, products || [], "created_at"),
        clients: calculateGrowth(15, clients || [], "created_at"),
        transactions: calculateGrowth(15, transactions || [], "created_at"),
      };

      const monthlyGrowth = {
        sales: calculateGrowth(30, sales, "created_at", "valor_liquido"),
        users: calculateGrowth(30, users || [], "created_at"),
        products: calculateGrowth(30, products || [], "created_at"),
        clients: calculateGrowth(30, clients || [], "created_at"),
        transactions: calculateGrowth(30, transactions || [], "created_at"),
      };

      // Frequência
      let averageDaysBetweenSales = 0;
      if ((sales || []).length > 1) {
        const sortedSales = [...(sales || [])].sort(
          (a: any, b: any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
        let totalDays = 0;
        for (let i = 1; i < sortedSales.length; i++) {
          const days = differenceInDays(new Date((sortedSales[i] as any).created_at), new Date((sortedSales[i - 1] as any).created_at));
          totalDays += days;
        }
        averageDaysBetweenSales = totalDays / (sortedSales.length - 1);
      }

      let averageDaysBetweenExpenses = 0;
      if ((expenses || []).length > 1) {
        const sortedExpenses = [...(expenses || [])].sort(
          (a: any, b: any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
        let totalDays = 0;
        for (let i = 1; i < sortedExpenses.length; i++) {
          const days = differenceInDays(
            new Date((sortedExpenses[i] as any).created_at),
            new Date((sortedExpenses[i - 1] as any).created_at)
          );
          totalDays += days;
        }
        averageDaysBetweenExpenses = totalDays / (sortedExpenses.length - 1);
      }

      // Dia e hora mais ativos
      const dayCountMap = new Map<string, number>();
      (transactions || []).forEach((t: any) => {
        const day = format(new Date(t.created_at), "EEEE", { locale: ptBR });
        dayCountMap.set(day, (dayCountMap.get(day) || 0) + 1);
      });
      const mostActiveDay = dayCountMap.size > 0
        ? Array.from(dayCountMap.entries()).sort((a, b) => b[1] - a[1])[0][0]
        : null;

      const mostActiveHour = hourMap.size > 0
        ? Array.from(hourMap.entries()).sort((a, b) => b[1] - a[1])[0][0]
        : null;

      // Categorias de produtos
      const categoryMap = new Map<string, number>();
      (products || []).forEach((p: any) => {
        if (p.categorias && Array.isArray(p.categorias)) {
          p.categorias.forEach((cat: string) => {
            categoryMap.set(cat, (categoryMap.get(cat) || 0) + 1);
          });
        }
      });
      const productCategories = Array.from(categoryMap.entries()).map(([category, count]) => ({
        category,
        count,
      }));

      // Categorias de despesas
      const expenseCategoryMap = new Map<string, { count: number; value: number }>();
      expenses.forEach((e: any) => {
        const category = e.categoria || "Sem categoria";
        const current = expenseCategoryMap.get(category) || { count: 0, value: 0 };
        expenseCategoryMap.set(category, {
          count: current.count + 1,
          value: current.value + Number(e.valor_liquido || 0),
        });
      });
      const expenseCategories = Array.from(expenseCategoryMap.entries()).map(([category, data]) => ({
        category,
        count: data.count,
        value: data.value,
      }));

      // Atividade semanal (últimas 8 semanas)
      const weeklyMap = new Map<string, { transactions: number; users: number }>();
      (transactions || []).forEach((t: any) => {
        const week = format(new Date(t.created_at), "dd/MM", { locale: ptBR });
        const current = weeklyMap.get(week) || { transactions: 0, users: 0 };
        weeklyMap.set(week, {
          transactions: current.transactions + 1,
          users: current.users,
        });
      });
      const weeklyActivity = Array.from(weeklyMap.entries())
        .map(([week, data]) => ({ week, ...data }))
        .slice(-8);

      // Atividade diária (últimos 30 dias)
      const dailyMap = new Map<string, { transactions: number; revenue: number }>();
      (sales || []).forEach((sale: any) => {
        const day = format(new Date(sale.created_at), "dd/MM", { locale: ptBR });
        const current = dailyMap.get(day) || { transactions: 0, revenue: 0 };
        dailyMap.set(day, {
          transactions: current.transactions + 1,
          revenue: current.revenue + Number(sale.valor_liquido || 0),
        });
      });
      const dailyActivity = Array.from(dailyMap.entries())
        .map(([day, data]) => ({ day, ...data }))
        .slice(-30);

      // Transações por mês
      const transactionMonthMap = new Map<string, { month: string; count: number; value: number }>();
      (transactions || []).forEach((t: any) => {
        const month = format(new Date(t.created_at), "MMM/yy", { locale: ptBR });
        const current = transactionMonthMap.get(month) || { month, count: 0, value: 0 };
        transactionMonthMap.set(month, {
          month,
          count: current.count + 1,
          value: current.value + Number(t.valor_liquido || 0),
        });
      });
      const transactionsByMonth = Array.from(transactionMonthMap.values()).slice(-12);

      // Scores e insights
      const daysSinceLastActivity = lastActivity ? differenceInDays(now, new Date(lastActivity)) : daysSinceCreation;
      const churnRisk = daysSinceLastActivity > 30 ? 100 : (daysSinceLastActivity / 30) * 100;
      
      const engagementScore = Math.min(
        100,
        (activityRate * 0.4) +
        (usersWithSales / Math.max(totalUsers, 1) * 100 * 0.3) +
        (productsWithSales / Math.max(totalProducts, 1) * 100 * 0.3)
      );

      const healthScore = Math.min(
        100,
        (profitMargin > 0 ? 30 : 0) +
        (salesGrowth > 0 ? 20 : 0) +
        (userGrowth > 0 ? 15 : 0) +
        (clientGrowth > 0 ? 15 : 0) +
        (engagementScore * 0.2)
      );

      const statsResult = {
        totalUsers,
        activeUsers,
        inactiveUsers,
        totalProducts,
        activeProducts,
        inactiveProducts,
        totalClients,
        totalSuppliers,
        totalTransactions,
        totalSales,
        totalExpenses,
        totalRevenue,
        totalExpenseValue,
        netProfit,
        profitMargin,
        averageTicket,
        averageExpense,
        totalSalesValue,
        totalSalesItems,
        averageItemsPerSale,
        salesGrowth,
        revenueGrowth,
        totalExpensesCount: totalExpenses,
        expensesGrowth,
        daysSinceCreation,
        daysActive,
        activityRate,
        lastActivity,
        averageTransactionsPerDay,
        averageUsersPerDay,
        monthlyRevenue,
        weeklyActivity,
        dailyActivity,
        hourlyDistribution,
        topUsers,
        topProducts,
        topClients,
        topSuppliers,
        productCategories,
        expenseCategories,
        paymentMethods,
        transactionStatus,
        userGrowth,
        productGrowth,
        clientGrowth,
        transactionGrowth,
        dailyGrowth,
        weeklyGrowth,
        biweeklyGrowth,
        monthlyGrowth,
        transactionsByDayOfWeek,
        transactionsByMonth,
        usersWithSales,
        usersWithoutSales,
        productsWithSales,
        productsWithoutSales,
        clientsWithPurchases,
        clientsWithoutPurchases,
        averageDaysBetweenSales,
        averageDaysBetweenExpenses,
        mostActiveDay,
        mostActiveHour,
        churnRisk,
        engagementScore,
        healthScore,
      } as TenantStats;

      console.log("TenantDetails: Estatísticas calculadas:", {
        totalUsers: statsResult.totalUsers,
        totalProducts: statsResult.totalProducts,
        totalClients: statsResult.totalClients,
        totalTransactions: statsResult.totalTransactions,
        totalRevenue: statsResult.totalRevenue,
        topUsers: statsResult.topUsers.length,
        topProducts: statsResult.topProducts.length,
        topClients: statsResult.topClients.length,
      });

      return statsResult;
    },
    enabled: !!allData && !!tenant && !!id,
  });

  const updateMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      const { error } = await supabase
        .from("tenants" as any)
        .update({
          name: formData.get("name") as string,
          email: (formData.get("email") as string) || null,
          status: (formData.get("status") as string) || "trial",
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenant", id] });
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
      toast.success("Tenant atualizado com sucesso");
      if (id) {
        void logCrud({ entitySlug: "entity:tenants", operationSlug: "update", entityId: id, screenSlug: "tenant-details" });
      }
      setIsEditing(false);
    },
    onError: () => toast.error("Erro ao atualizar tenant"),
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    updateMutation.mutate(formData);
  };

  if (tenantLoading || dataLoading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!tenant) {
    return <div className="p-8">Cliente não encontrado</div>;
  }

  // Se allData ainda não foi carregado, mostrar loading
  if (!allData && !dataLoading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando dados do cliente...</p>
        </div>
      </div>
    );
  }

  // Se stats ainda não foi calculado, mostrar loading
  if (!stats && !statsLoading && allData) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Calculando estatísticas...</p>
        </div>
      </div>
    );
  }

  const COLORS = [
    "#3b82f6", // Azul forte
    "#10b981", // Verde forte
    "#f59e0b", // Laranja forte
    "#ef4444", // Vermelho forte
    "#8b5cf6", // Roxo forte
    "#ec4899", // Rosa forte
    "#06b6d4", // Ciano forte
    "#14b8a6", // Teal forte
    "#f97316", // Laranja escuro
    "#6366f1", // Índigo forte
  ];

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      trial: "secondary",
      active: "default",
      suspended: "destructive",
      cancelled: "outline",
    };
    return variants[status] || "outline";
  };

  const getHealthColor = (score: number) => {
    if (score >= 80) return "text-green-600";
    if (score >= 60) return "text-yellow-600";
    return "text-red-600";
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      {!isEmbedded && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/tenants")}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-lg bg-primary/10 flex items-center justify-center">
                <Building2 className="h-8 w-8 text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-bold">{tenant.name}</h1>
                <p className="text-muted-foreground">
                  Cliente desde {format(new Date(tenant.created_at), "dd/MM/yyyy")}
                  {tenant.slug && ` • ${tenant.slug}`}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* KPIs Cards - Primeira Linha */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Receita Total</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats ? formatCurrency(stats.totalRevenue) : "-"}
            </div>
            {stats && stats.revenueGrowth !== 0 && (
              <p className={`text-xs mt-1 ${stats?.revenueGrowth > 0 ? "text-green-600" : "text-red-600"}`}>
                {stats?.revenueGrowth > 0 ? "↑" : "↓"} {Math.abs(stats?.revenueGrowth || 0).toFixed(1)}% vs período anterior
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Lucro Líquido</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats ? formatCurrency(stats.netProfit) : "-"}
            </div>
            {stats && (
              <p className="text-xs text-muted-foreground mt-1">
                Margem: {stats?.profitMargin?.toFixed(1) || "0.0"}%
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Vendas</CardTitle>
            <ShoppingCart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalSales || 0}</div>
            {stats && (
              <p className="text-xs text-muted-foreground mt-1">
                Ticket médio: {formatCurrency(stats?.averageTicket)}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Usuários Ativos</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.activeUsers || 0}</div>
            {stats && (
              <p className="text-xs text-muted-foreground mt-1">
                Total: {stats?.totalUsers || 0} usuários
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* KPIs Cards - Segunda Linha */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Produtos</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalProducts || 0}</div>
            {stats && (
              <p className="text-xs text-muted-foreground mt-1">
                {stats?.activeProducts || 0} ativos • {stats?.productsWithSales || 0} com vendas
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Clientes</CardTitle>
            <UserCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalClients || 0}</div>
            {stats && (
              <p className="text-xs text-muted-foreground mt-1">
                {stats?.clientsWithPurchases || 0} com compras
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Taxa de Atividade</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats ? `${stats?.activityRate?.toFixed(1) || "0.0"}%` : "-"}</div>
            {stats && (
              <p className="text-xs text-muted-foreground mt-1">
                {stats?.daysActive || 0} de {stats?.daysSinceCreation || 0} dias
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Score de Saúde</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${stats ? getHealthColor(stats.healthScore) : ""}`}>
              {stats ? `${stats.healthScore.toFixed(0)}/100` : "-"}
            </div>
            {stats && (
              <p className="text-xs text-muted-foreground mt-1">
                Engajamento: {stats?.engagementScore?.toFixed(0) || "0"}%
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tabs Content */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="grid w-full grid-cols-8">
          <TabsTrigger value="overview">Visão Geral</TabsTrigger>
          <TabsTrigger value="financial">Financeiro</TabsTrigger>
          <TabsTrigger value="sales">Vendas</TabsTrigger>
          <TabsTrigger value="users">Usuários</TabsTrigger>
          <TabsTrigger value="products">Produtos</TabsTrigger>
          <TabsTrigger value="clients">Clientes</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="data">Dados</TabsTrigger>
        </TabsList>

        {/* Visão Geral */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Informações Básicas */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Informações do Tenant
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Status</span>
                  <Badge variant={getStatusBadge(tenant.status)}>{tenant.status}</Badge>
                </div>
                {tenant.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span>{tenant.email}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Criado em</span>
                  <span>{format(new Date(tenant.created_at), "dd/MM/yyyy HH:mm")}</span>
                </div>
                {tenant.trial_ends_at && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-muted-foreground">Trial até</span>
                    <span>{format(new Date(tenant.trial_ends_at), "dd/MM/yyyy")}</span>
                  </div>
                )}
                {tenant.subscription_plan_id && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-muted-foreground">Plano</span>
                    <Badge variant="outline">{tenant.subscription_plan_id}</Badge>
                  </div>
                )}
                <Separator />
                <div>
                  <p className="text-sm font-medium mb-2">Resumo de Uso</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Dias desde criação:</span>
                      <span className="font-medium">{stats?.daysSinceCreation || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Dias ativos:</span>
                      <span className="font-medium">{stats?.daysActive || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Última atividade:</span>
                      <span className="font-medium">
                      {stats?.lastActivity
                        ? format(new Date(stats?.lastActivity), "dd/MM/yyyy")
                        : "Nunca"}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Top Performers */}
            <Card>
              <CardHeader>
                <CardTitle>Top 5 Vendedores</CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.topUsers && stats.topUsers.length > 0 ? (
                  <div className="space-y-3">
                    {stats.topUsers.slice(0, 5).map((user, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{user.name}</p>
                          <p className="text-sm text-muted-foreground">{user.transactions} vendas</p>
                        </div>
                        <Badge variant="secondary">{formatCurrency(user.sales)}</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-4">Nenhum vendedor registrado</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Gráficos Principais */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Receita vs Despesas Mensal */}
            <Card>
              <CardHeader>
                <CardTitle>Receita vs Despesas (Mensal)</CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.monthlyRevenue && stats.monthlyRevenue.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <ComposedChart data={stats.monthlyRevenue} margin={{ top: 10, right: 30, left: 20, bottom: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis width={80} />
                      <Tooltip formatter={(value: number) => formatCurrency(value)} />
                      <Legend />
                      <Bar dataKey="revenue" fill="#3b82f6" name="Receita" />
                      <Bar dataKey="expenses" fill="#ef4444" name="Despesas" />
                      <Line type="monotone" dataKey="profit" stroke="#10b981" strokeWidth={2} name="Lucro" />
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-muted-foreground text-center py-12">Sem dados disponíveis</p>
                )}
              </CardContent>
            </Card>

            {/* Distribuição por Dia da Semana */}
            <Card>
              <CardHeader>
                <CardTitle>Atividade por Dia da Semana</CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.transactionsByDayOfWeek && stats.transactionsByDayOfWeek.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={stats.transactionsByDayOfWeek} margin={{ top: 10, right: 30, left: 20, bottom: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" />
                      <YAxis width={60} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#3b82f6" name="Transações" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-muted-foreground text-center py-12">Sem dados disponíveis</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Financeiro */}
        <TabsContent value="financial" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Resumo Financeiro</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Receita Total</span>
                  <span className="text-lg font-bold text-green-600">
                    {stats ? formatCurrency(stats.totalRevenue) : "-"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Despesas Total</span>
                  <span className="text-lg font-bold text-red-600">
                    {stats ? formatCurrency(stats.totalExpenseValue) : "-"}
                  </span>
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Lucro Líquido</span>
                  <span className={`text-lg font-bold ${stats && stats.netProfit >= 0 ? "text-green-600" : "text-red-600"}`}>
                    {stats ? formatCurrency(stats.netProfit) : "-"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Margem de Lucro</span>
                  <span className="text-lg font-semibold">
                    {stats ? `${stats.profitMargin.toFixed(1)}%` : "-"}
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Métodos de Pagamento</CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.paymentMethods && stats.paymentMethods.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <RechartsPieChart margin={{ top: 40, right: 10, bottom: 10, left: 10 }}>
                      <Pie
                        data={stats.paymentMethods}
                        dataKey="value"
                        nameKey="method"
                        cx="50%"
                        cy="45%"
                        outerRadius={65}
                        label={(entry: any) => formatCurrency(entry.value)}
                        labelLine={{ strokeWidth: 1 } as any}
                      >
                        {stats.paymentMethods.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value: number) => formatCurrency(value)} />
                      <Legend wrapperStyle={{ paddingTop: '20px' }} />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-muted-foreground text-center py-12">Sem dados de pagamento</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Status de Transações</CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.transactionStatus && stats.transactionStatus.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <RechartsPieChart margin={{ top: 40, right: 10, bottom: 10, left: 10 }}>
                      <Pie
                        data={stats.transactionStatus}
                        dataKey="count"
                        nameKey="status"
                        cx="50%"
                        cy="45%"
                        outerRadius={65}
                        label={(entry: any) => entry.value}
                        labelLine={{ strokeWidth: 1 } as any}
                      >
                        {stats.transactionStatus.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend wrapperStyle={{ paddingTop: '20px' }} />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-muted-foreground text-center py-12">Sem dados disponíveis</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Evolução Financeira */}
          <Card>
            <CardHeader>
              <CardTitle>Evolução Financeira (12 meses)</CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.monthlyRevenue && stats.monthlyRevenue.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <AreaChart data={stats.monthlyRevenue} margin={{ top: 10, right: 30, left: 20, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis width={80} />
                    <Tooltip formatter={(value: number) => formatCurrency(value)} />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="revenue"
                      stackId="1"
                      stroke="#3b82f6"
                      fill="#3b82f6"
                      name="Receita"
                    />
                    <Area
                      type="monotone"
                      dataKey="expenses"
                      stackId="2"
                      stroke="#ef4444"
                      fill="#ef4444"
                      name="Despesas"
                    />
                    <Line
                      type="monotone"
                      dataKey="profit"
                      stroke="#10b981"
                      strokeWidth={3}
                      name="Lucro"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-muted-foreground text-center py-12">Sem dados disponíveis</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Vendas */}
        <TabsContent value="sales" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Top 10 Produtos Vendidos</CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.topProducts && stats.topProducts.length > 0 ? (
                  <div className="space-y-3">
                    {stats.topProducts.map((product, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{product.name}</p>
                          <p className="text-sm text-muted-foreground">{product.quantity}x unidades</p>
                        </div>
                        <Badge variant="secondary">{formatCurrency(product.revenue)}</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-4">Nenhum produto vendido</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Top 10 Clientes</CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.topClients && stats.topClients.length > 0 ? (
                  <div className="space-y-3">
                    {stats.topClients.map((client, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{client.name}</p>
                          <p className="text-sm text-muted-foreground">{client.purchases} compras</p>
                        </div>
                        <Badge variant="secondary">{formatCurrency(client.value)}</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-4">Nenhum cliente registrado</p>
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Evolução de Vendas (Diária - Últimos 30 dias)</CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.dailyActivity && stats.dailyActivity.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={stats.dailyActivity} margin={{ top: 10, right: 30, left: 20, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="day" />
                    <YAxis width={80} />
                    <Tooltip formatter={(value: number) => formatCurrency(value)} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="revenue"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      name="Receita (R$)"
                    />
                    <Line
                      type="monotone"
                      dataKey="transactions"
                      stroke="#f59e0b"
                      strokeWidth={2}
                      name="Transações"
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-muted-foreground text-center py-12">Sem dados de vendas</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Distribuição de Vendas por Hora</CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.hourlyDistribution && stats.hourlyDistribution.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={stats.hourlyDistribution} margin={{ top: 10, right: 30, left: 20, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hour" />
                    <YAxis width={60} />
                    <Tooltip />
                    <Bar dataKey="transactions" fill="#3b82f6" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-muted-foreground text-center py-12">Sem dados disponíveis</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Usuários */}
        <TabsContent value="users" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Resumo de Usuários</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Total</span>
                  <span className="text-2xl font-bold">{stats?.totalUsers || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Ativos</span>
                  <span className="text-lg font-semibold text-green-600">{stats?.activeUsers || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Inativos</span>
                  <span className="text-lg font-semibold text-red-600">{stats?.inactiveUsers || 0}</span>
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Com vendas</span>
                  <span className="text-lg font-semibold">{stats?.usersWithSales || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Sem vendas</span>
                  <span className="text-lg font-semibold">{stats?.usersWithoutSales || 0}</span>
                </div>
                {stats && stats.userGrowth !== 0 && (
                  <>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-muted-foreground">Crescimento</span>
                      <span className={`text-lg font-semibold ${stats.userGrowth > 0 ? "text-green-600" : "text-red-600"}`}>
                        {stats.userGrowth > 0 ? "↑" : "↓"} {Math.abs(stats.userGrowth).toFixed(1)}%
                      </span>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Top 10 Vendedores</CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.topUsers && stats.topUsers.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={stats.topUsers} margin={{ top: 10, right: 30, left: 20, bottom: 80 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                      <YAxis width={80} />
                      <Tooltip formatter={(value: number) => formatCurrency(value)} />
                      <Bar dataKey="sales" fill="#3b82f6" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-muted-foreground text-center py-12">Sem dados disponíveis</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Performance de Vendedores</CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.topUsers && stats.topUsers.length > 0 ? (
                  <div className="space-y-3">
                    {stats.topUsers.slice(0, 5).map((user, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{user.name}</p>
                          <p className="text-sm text-muted-foreground">{user.transactions} vendas</p>
                        </div>
                        <Badge variant="secondary">{formatCurrency(user.sales)}</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-4">Nenhum vendedor registrado</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Produtos */}
        <TabsContent value="products" className="space-y-4">
          {/* Primeira linha: Resumo de Produtos e Top 10 Produtos */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Resumo de Produtos</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Total</span>
                  <span className="text-2xl font-bold">{stats?.totalProducts || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Ativos</span>
                  <span className="text-lg font-semibold text-green-600">{stats?.activeProducts || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Inativos</span>
                  <span className="text-lg font-semibold text-red-600">{stats?.inactiveProducts || 0}</span>
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Com vendas</span>
                  <span className="text-lg font-semibold">{stats?.productsWithSales || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Sem vendas</span>
                  <span className="text-lg font-semibold">{stats?.productsWithoutSales || 0}</span>
                </div>
                {stats && stats.productGrowth !== 0 && (
                  <>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-muted-foreground">Crescimento</span>
                      <span className={`text-lg font-semibold ${stats.productGrowth > 0 ? "text-green-600" : "text-red-600"}`}>
                        {stats.productGrowth > 0 ? "↑" : "↓"} {Math.abs(stats.productGrowth).toFixed(1)}%
                      </span>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Top 10 Produtos (Receita)</CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.topProducts && stats.topProducts.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={stats.topProducts} margin={{ top: 20, right: 40, left: 40, bottom: 100 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" angle={-45} textAnchor="end" height={120} />
                      <YAxis width={80} />
                      <Tooltip formatter={(value: number) => formatCurrency(value)} />
                      <Bar dataKey="revenue" fill="#3b82f6" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-muted-foreground text-center py-12">Sem dados disponíveis</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Segunda linha: Categorias de Produtos sozinho */}
          <Card>
            <CardHeader>
              <CardTitle>Categorias de Produtos</CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.productCategories && stats.productCategories.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <RechartsPieChart margin={{ top: 60, right: 40, bottom: 40, left: 40 }}>
                    <Pie
                      data={stats.productCategories}
                      dataKey="count"
                      nameKey="category"
                      cx="50%"
                      cy="50%"
                      outerRadius={120}
                      label={(entry: any) => entry.value}
                      labelLine={{ strokeWidth: 1 } as any}
                    >
                      {stats.productCategories.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend wrapperStyle={{ paddingTop: '30px' }} />
                  </RechartsPieChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-muted-foreground text-center py-12">Sem categorias cadastradas</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Clientes */}
        <TabsContent value="clients" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Resumo de Clientes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Total</span>
                  <span className="text-2xl font-bold">{stats?.totalClients || 0}</span>
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Com compras</span>
                  <span className="text-lg font-semibold text-green-600">{stats?.clientsWithPurchases || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Sem compras</span>
                  <span className="text-lg font-semibold text-red-600">{stats?.clientsWithoutPurchases || 0}</span>
                </div>
                {stats && stats.clientGrowth !== 0 && (
                  <>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-muted-foreground">Crescimento</span>
                      <span className={`text-lg font-semibold ${stats.clientGrowth > 0 ? "text-green-600" : "text-red-600"}`}>
                        {stats.clientGrowth > 0 ? "↑" : "↓"} {Math.abs(stats.clientGrowth).toFixed(1)}%
                      </span>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Top 10 Clientes (Valor)</CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.topClients && stats.topClients.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={stats.topClients} margin={{ top: 10, right: 30, left: 20, bottom: 80 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                      <YAxis width={80} />
                      <Tooltip formatter={(value: number) => formatCurrency(value)} />
                      <Bar dataKey="value" fill="#3b82f6" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-muted-foreground text-center py-12">Sem dados disponíveis</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Analytics */}
        <TabsContent value="analytics" className="space-y-4">
          {/* Primeira linha: Crescimento Diário e Semanal */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Crescimento Diário */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Análise de Crescimento Diário</CardTitle>
                <p className="text-xs text-muted-foreground mt-1">Último 1 dia vs 1 dia anterior</p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Vendas</span>
                  <span className={`text-sm font-semibold ${stats && stats.dailyGrowth?.sales > 0 ? "text-green-600" : stats?.dailyGrowth?.sales < 0 ? "text-red-600" : ""}`}>
                    {stats?.dailyGrowth ? `${stats.dailyGrowth.sales > 0 ? "↑" : stats.dailyGrowth.sales < 0 ? "↓" : "→"} ${Math.abs(stats.dailyGrowth.sales).toFixed(1)}%` : "-"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Usuários</span>
                  <span className={`text-sm font-semibold ${stats && stats.dailyGrowth?.users > 0 ? "text-green-600" : stats?.dailyGrowth?.users < 0 ? "text-red-600" : ""}`}>
                    {stats?.dailyGrowth ? `${stats.dailyGrowth.users > 0 ? "↑" : stats.dailyGrowth.users < 0 ? "↓" : "→"} ${Math.abs(stats.dailyGrowth.users).toFixed(1)}%` : "-"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Produtos</span>
                  <span className={`text-sm font-semibold ${stats && stats.dailyGrowth?.products > 0 ? "text-green-600" : stats?.dailyGrowth?.products < 0 ? "text-red-600" : ""}`}>
                    {stats?.dailyGrowth ? `${stats.dailyGrowth.products > 0 ? "↑" : stats.dailyGrowth.products < 0 ? "↓" : "→"} ${Math.abs(stats.dailyGrowth.products).toFixed(1)}%` : "-"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Clientes</span>
                  <span className={`text-sm font-semibold ${stats && stats.dailyGrowth?.clients > 0 ? "text-green-600" : stats?.dailyGrowth?.clients < 0 ? "text-red-600" : ""}`}>
                    {stats?.dailyGrowth ? `${stats.dailyGrowth.clients > 0 ? "↑" : stats.dailyGrowth.clients < 0 ? "↓" : "→"} ${Math.abs(stats.dailyGrowth.clients).toFixed(1)}%` : "-"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Transações</span>
                  <span className={`text-sm font-semibold ${stats && stats.dailyGrowth?.transactions > 0 ? "text-green-600" : stats?.dailyGrowth?.transactions < 0 ? "text-red-600" : ""}`}>
                    {stats?.dailyGrowth ? `${stats.dailyGrowth.transactions > 0 ? "↑" : stats.dailyGrowth.transactions < 0 ? "↓" : "→"} ${Math.abs(stats.dailyGrowth.transactions).toFixed(1)}%` : "-"}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Crescimento Semanal */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Análise de Crescimento Semanal</CardTitle>
                <p className="text-xs text-muted-foreground mt-1">Últimos 7 dias vs 7 dias anteriores</p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Vendas</span>
                  <span className={`text-sm font-semibold ${stats && stats.weeklyGrowth?.sales > 0 ? "text-green-600" : stats?.weeklyGrowth?.sales < 0 ? "text-red-600" : ""}`}>
                    {stats?.weeklyGrowth ? `${stats.weeklyGrowth.sales > 0 ? "↑" : stats.weeklyGrowth.sales < 0 ? "↓" : "→"} ${Math.abs(stats.weeklyGrowth.sales).toFixed(1)}%` : "-"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Usuários</span>
                  <span className={`text-sm font-semibold ${stats && stats.weeklyGrowth?.users > 0 ? "text-green-600" : stats?.weeklyGrowth?.users < 0 ? "text-red-600" : ""}`}>
                    {stats?.weeklyGrowth ? `${stats.weeklyGrowth.users > 0 ? "↑" : stats.weeklyGrowth.users < 0 ? "↓" : "→"} ${Math.abs(stats.weeklyGrowth.users).toFixed(1)}%` : "-"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Produtos</span>
                  <span className={`text-sm font-semibold ${stats && stats.weeklyGrowth?.products > 0 ? "text-green-600" : stats?.weeklyGrowth?.products < 0 ? "text-red-600" : ""}`}>
                    {stats?.weeklyGrowth ? `${stats.weeklyGrowth.products > 0 ? "↑" : stats.weeklyGrowth.products < 0 ? "↓" : "→"} ${Math.abs(stats.weeklyGrowth.products).toFixed(1)}%` : "-"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Clientes</span>
                  <span className={`text-sm font-semibold ${stats && stats.weeklyGrowth?.clients > 0 ? "text-green-600" : stats?.weeklyGrowth?.clients < 0 ? "text-red-600" : ""}`}>
                    {stats?.weeklyGrowth ? `${stats.weeklyGrowth.clients > 0 ? "↑" : stats.weeklyGrowth.clients < 0 ? "↓" : "→"} ${Math.abs(stats.weeklyGrowth.clients).toFixed(1)}%` : "-"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Transações</span>
                  <span className={`text-sm font-semibold ${stats && stats.weeklyGrowth?.transactions > 0 ? "text-green-600" : stats?.weeklyGrowth?.transactions < 0 ? "text-red-600" : ""}`}>
                    {stats?.weeklyGrowth ? `${stats.weeklyGrowth.transactions > 0 ? "↑" : stats.weeklyGrowth.transactions < 0 ? "↓" : "→"} ${Math.abs(stats.weeklyGrowth.transactions).toFixed(1)}%` : "-"}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Segunda linha: Crescimento Quinzenal e Mensal */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Crescimento Quinzenal */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Análise de Crescimento Quinzenal</CardTitle>
                <p className="text-xs text-muted-foreground mt-1">Últimos 15 dias vs 15 dias anteriores</p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Vendas</span>
                  <span className={`text-sm font-semibold ${stats && stats.biweeklyGrowth?.sales > 0 ? "text-green-600" : stats?.biweeklyGrowth?.sales < 0 ? "text-red-600" : ""}`}>
                    {stats?.biweeklyGrowth ? `${stats.biweeklyGrowth.sales > 0 ? "↑" : stats.biweeklyGrowth.sales < 0 ? "↓" : "→"} ${Math.abs(stats.biweeklyGrowth.sales).toFixed(1)}%` : "-"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Usuários</span>
                  <span className={`text-sm font-semibold ${stats && stats.biweeklyGrowth?.users > 0 ? "text-green-600" : stats?.biweeklyGrowth?.users < 0 ? "text-red-600" : ""}`}>
                    {stats?.biweeklyGrowth ? `${stats.biweeklyGrowth.users > 0 ? "↑" : stats.biweeklyGrowth.users < 0 ? "↓" : "→"} ${Math.abs(stats.biweeklyGrowth.users).toFixed(1)}%` : "-"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Produtos</span>
                  <span className={`text-sm font-semibold ${stats && stats.biweeklyGrowth?.products > 0 ? "text-green-600" : stats?.biweeklyGrowth?.products < 0 ? "text-red-600" : ""}`}>
                    {stats?.biweeklyGrowth ? `${stats.biweeklyGrowth.products > 0 ? "↑" : stats.biweeklyGrowth.products < 0 ? "↓" : "→"} ${Math.abs(stats.biweeklyGrowth.products).toFixed(1)}%` : "-"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Clientes</span>
                  <span className={`text-sm font-semibold ${stats && stats.biweeklyGrowth?.clients > 0 ? "text-green-600" : stats?.biweeklyGrowth?.clients < 0 ? "text-red-600" : ""}`}>
                    {stats?.biweeklyGrowth ? `${stats.biweeklyGrowth.clients > 0 ? "↑" : stats.biweeklyGrowth.clients < 0 ? "↓" : "→"} ${Math.abs(stats.biweeklyGrowth.clients).toFixed(1)}%` : "-"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Transações</span>
                  <span className={`text-sm font-semibold ${stats && stats.biweeklyGrowth?.transactions > 0 ? "text-green-600" : stats?.biweeklyGrowth?.transactions < 0 ? "text-red-600" : ""}`}>
                    {stats?.biweeklyGrowth ? `${stats.biweeklyGrowth.transactions > 0 ? "↑" : stats.biweeklyGrowth.transactions < 0 ? "↓" : "→"} ${Math.abs(stats.biweeklyGrowth.transactions).toFixed(1)}%` : "-"}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Crescimento Mensal */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Análise de Crescimento Mensal</CardTitle>
                <p className="text-xs text-muted-foreground mt-1">Últimos 30 dias vs 30 dias anteriores</p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Vendas</span>
                  <span className={`text-sm font-semibold ${stats && stats.monthlyGrowth?.sales > 0 ? "text-green-600" : stats?.monthlyGrowth?.sales < 0 ? "text-red-600" : ""}`}>
                    {stats?.monthlyGrowth ? `${stats.monthlyGrowth.sales > 0 ? "↑" : stats.monthlyGrowth.sales < 0 ? "↓" : "→"} ${Math.abs(stats.monthlyGrowth.sales).toFixed(1)}%` : "-"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Usuários</span>
                  <span className={`text-sm font-semibold ${stats && stats.monthlyGrowth?.users > 0 ? "text-green-600" : stats?.monthlyGrowth?.users < 0 ? "text-red-600" : ""}`}>
                    {stats?.monthlyGrowth ? `${stats.monthlyGrowth.users > 0 ? "↑" : stats.monthlyGrowth.users < 0 ? "↓" : "→"} ${Math.abs(stats.monthlyGrowth.users).toFixed(1)}%` : "-"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Produtos</span>
                  <span className={`text-sm font-semibold ${stats && stats.monthlyGrowth?.products > 0 ? "text-green-600" : stats?.monthlyGrowth?.products < 0 ? "text-red-600" : ""}`}>
                    {stats?.monthlyGrowth ? `${stats.monthlyGrowth.products > 0 ? "↑" : stats.monthlyGrowth.products < 0 ? "↓" : "→"} ${Math.abs(stats.monthlyGrowth.products).toFixed(1)}%` : "-"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Clientes</span>
                  <span className={`text-sm font-semibold ${stats && stats.monthlyGrowth?.clients > 0 ? "text-green-600" : stats?.monthlyGrowth?.clients < 0 ? "text-red-600" : ""}`}>
                    {stats?.monthlyGrowth ? `${stats.monthlyGrowth.clients > 0 ? "↑" : stats.monthlyGrowth.clients < 0 ? "↓" : "→"} ${Math.abs(stats.monthlyGrowth.clients).toFixed(1)}%` : "-"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Transações</span>
                  <span className={`text-sm font-semibold ${stats && stats.monthlyGrowth?.transactions > 0 ? "text-green-600" : stats?.monthlyGrowth?.transactions < 0 ? "text-red-600" : ""}`}>
                    {stats?.monthlyGrowth ? `${stats.monthlyGrowth.transactions > 0 ? "↑" : stats.monthlyGrowth.transactions < 0 ? "↓" : "→"} ${Math.abs(stats.monthlyGrowth.transactions).toFixed(1)}%` : "-"}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Terceira linha: Análise de Frequência e Insights e Alertas */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Análise de Frequência</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Média entre vendas</span>
                  <span className="text-lg font-semibold">
                    {stats ? `${Math.round(stats.averageDaysBetweenSales)} dias` : "-"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Média entre despesas</span>
                  <span className="text-lg font-semibold">
                    {stats ? `${Math.round(stats.averageDaysBetweenExpenses)} dias` : "-"}
                  </span>
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Dia mais ativo</span>
                  <span className="text-lg font-semibold">{stats?.mostActiveDay || "-"}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Hora mais ativa</span>
                  <span className="text-lg font-semibold">
                    {stats?.mostActiveHour !== null && stats?.mostActiveHour !== undefined ? `${stats?.mostActiveHour}h` : "-"}
                  </span>
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Transações/dia</span>
                  <span className="text-lg font-semibold">
                    {stats ? stats.averageTransactionsPerDay.toFixed(1) : "-"}
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Insights e Alertas</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Risco de Churn</span>
                  <span className={`text-lg font-semibold ${stats && stats.churnRisk > 50 ? "text-red-600" : "text-green-600"}`}>
                    {stats ? `${stats.churnRisk.toFixed(0)}%` : "-"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Score de Engajamento</span>
                  <span className={`text-lg font-semibold ${stats ? getHealthColor(stats.engagementScore) : ""}`}>
                    {stats ? `${stats.engagementScore.toFixed(0)}/100` : "-"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Score de Saúde</span>
                  <span className={`text-lg font-semibold ${stats ? getHealthColor(stats.healthScore) : ""}`}>
                    {stats ? `${stats.healthScore.toFixed(0)}/100` : "-"}
                  </span>
                </div>
                <Separator />
                {stats && stats.churnRisk > 50 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm text-red-800 font-medium">⚠️ Atenção: Risco de churn alto</p>
                    <p className="text-xs text-red-600 mt-1">
                      Última atividade há {stats.lastActivity ? differenceInDays(new Date(), new Date(stats.lastActivity)) : 0} dias
                    </p>
                  </div>
                )}
                {stats && stats.healthScore < 60 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-sm text-yellow-800 font-medium">⚠️ Score de saúde baixo</p>
                    <p className="text-xs text-yellow-600 mt-1">Recomenda-se análise detalhada</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Gráficos de Analytics */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Transações por Mês</CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.transactionsByMonth && stats.transactionsByMonth.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={stats.transactionsByMonth} margin={{ top: 10, right: 30, left: 20, bottom: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis yAxisId="left" width={60} />
                      <YAxis yAxisId="right" orientation="right" width={80} />
                      <Tooltip />
                      <Legend />
                      <Bar yAxisId="left" dataKey="count" fill="#3b82f6" name="Quantidade" />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="value"
                        stroke="#f59e0b"
                        strokeWidth={2}
                        name="Valor (R$)"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-muted-foreground text-center py-12">Sem dados disponíveis</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Categorias de Despesas</CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.expenseCategories && stats.expenseCategories.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={stats.expenseCategories} margin={{ top: 10, right: 30, left: 20, bottom: 80 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="category" angle={-45} textAnchor="end" height={100} />
                      <YAxis width={80} />
                      <Tooltip formatter={(value: number) => formatCurrency(value)} />
                      <Bar dataKey="value" fill="#ef4444" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-muted-foreground text-center py-12">Sem categorias de despesas</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Dados */}
        <TabsContent value="data" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Dados do Tenant</CardTitle>
              {!isEditing && (
                <Button variant="outline" onClick={() => setIsEditing(true)}>
                  Editar
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {isEditing ? (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Nome *</Label>
                      <Input id="name" name="name" defaultValue={tenant.name} required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input id="email" name="email" type="email" defaultValue={tenant.email || ""} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="status">Status</Label>
                      <select
                        id="status"
                        name="status"
                        defaultValue={tenant.status}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                      >
                        <option value="trial">Trial</option>
                        <option value="active">Ativo</option>
                        <option value="suspended">Suspenso</option>
                        <option value="cancelled">Cancelado</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" disabled={updateMutation.isPending}>
                      <Save className="mr-2 h-4 w-4" />
                      Salvar Alterações
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setIsEditing(false)}>
                      Cancelar
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Nome</p>
                      <p>{tenant.name}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Slug</p>
                      <code className="text-sm bg-muted px-2 py-1 rounded">{tenant.slug}</code>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Email</p>
                      <p>{tenant.email || "-"}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Status</p>
                      <Badge variant={getStatusBadge(tenant.status)}>{tenant.status}</Badge>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Criado em</p>
                      <p>{format(new Date(tenant.created_at), "dd/MM/yyyy HH:mm")}</p>
                    </div>
                    {tenant.trial_ends_at && (
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Trial até</p>
                        <p>{format(new Date(tenant.trial_ends_at), "dd/MM/yyyy")}</p>
                      </div>
                    )}
                    {tenant.subscription_plan_id && (
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Plano</p>
                        <Badge variant="outline">{tenant.subscription_plan_id}</Badge>
                      </div>
                    )}
                    {tenant.subscription_type && (
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Tipo de Assinatura</p>
                        <p>{tenant.subscription_type}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

import { logCrud } from "@/services/activities";
